import { useState } from 'react';
import { SettingsIcon, CircleHelpIcon, UserCircleIcon, LogOutIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import NavigationItems from './NavigationItems';
import type { NavigationProps, MenuItem } from './NavigationItems';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface NavigationItemsProps extends NavigationProps {
  slug: string | undefined;
  collapsed?: boolean;
}

const FooterNavigation = ({ slug, activePathname, collapsed = false }: NavigationItemsProps) => {
  const { t } = useTranslation('common');

  const menus: MenuItem[] = [
    {
      name: t('help'),
      href: activePathname || '/',
      icon: CircleHelpIcon,
      active: false,
    },
    {
      name: t('matter-settings'),
      href: `/matter/${slug}/settings`,
      icon: SettingsIcon,
      active:
        activePathname?.startsWith(`/matter/${slug}`) &&
        activePathname.includes('settings'),
    },
  ];

  return (
    <div>
      <NavigationItems menus={menus} collapsed={collapsed} />
      <AccountDropdown collapsed={collapsed} activePathname={activePathname} />
    </div>
  );
};

const AccountDropdown = ({ collapsed, activePathname }: { collapsed: boolean; activePathname: string | null }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // The auth provider displays the API error.
    }
  };

  const isActive = activePathname === '/settings/account';

  const trigger = (
    <button
      type="button"
      className={cn(
        "group flex items-center rounded-lg text-[13px] transition-all duration-200 w-full mt-0.5",
        collapsed
          ? "justify-center p-2 mx-auto w-10 h-10"
          : "px-2.5 py-[7px] gap-2.5",
        isActive
          ? 'bg-white/15 text-white font-medium shadow-[0_1px_3px_rgba(0,0,0,0.2)]'
          : 'text-zinc-100 hover:text-white hover:bg-white/10',
      )}
    >
      <UserCircleIcon className={cn(
        'h-[18px] w-[18px] shrink-0 transition-colors duration-200',
        isActive ? 'text-crimson-300' : 'text-zinc-200 group-hover:text-crimson-300'
      )} strokeWidth={1.5} aria-hidden="true" />
      {!collapsed && <span>{t('account')}</span>}
    </button>
  );

  const dropdownMenu = (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? 'right' : 'top'}
        align={collapsed ? 'center' : 'start'}
        className="w-48 p-1 shadow-lg border-white/10 bg-[hsl(221,36%,20%)] text-white rounded-lg"
      >
        <DropdownMenuItem
          onClick={() => navigate({ to: '/settings/account' })}
          className="cursor-pointer rounded-md text-[13px] h-8 gap-2.5 text-zinc-200 focus:bg-white/10 focus:text-white"
        >
          <UserCircleIcon className="w-4 h-4 text-zinc-400" />
          User settings
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-zinc-300 rounded-md text-[13px] h-8 gap-2.5 focus:bg-white/10 focus:text-white"
        >
          <LogOutIcon className="w-4 h-4 text-zinc-400" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip open={dropdownOpen ? false : undefined}>
          <TooltipTrigger asChild>
            <div>{dropdownMenu}</div>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs font-medium bg-zinc-900 text-white border-zinc-800 shadow-lg">
            {t('account')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return dropdownMenu;
};

export default FooterNavigation;
